import { useEffect, useRef, useState } from "react";

const supported = () => typeof window !== "undefined" && "Notification" in window;

export const askNotificationPermission = () => {
  try {
    if (supported() && Notification.permission === "default") Notification.requestPermission();
  } catch {
    return;
  }
};

const resultText = (upload) => {
  const done = upload.done || 0;
  const failed = upload.failed || 0;
  if (failed && !done) return failed === 1 ? "the input failed" : `all ${failed} inputs failed`;
  return failed ? `${done} done, ${failed} failed` : `${done} done`;
};

const awayFromPage = () => document.hidden || !document.hasFocus();

export default function useFinishedNotifications(uploads, { isActive, nameOf, onOpen }) {
  const previous = useRef(null);
  const baseTitle = useRef(document.title);
  const onOpenRef = useRef(onOpen);
  const [finished, setFinished] = useState([]);
  const [unseen, setUnseen] = useState(0);

  onOpenRef.current = onOpen;

  useEffect(() => {
    const list = uploads.filter(Boolean);
    const known = previous.current;
    previous.current = new Map(list.map((upload) => [upload.id, isActive(upload)]));
    if (!known) return;

    const ended = list.filter((upload) => known.get(upload.id) === true && !isActive(upload));
    if (!ended.length) return;

    const items = ended.map((upload) => ({
      id: upload.id,
      name: nameOf(upload),
      text: resultText(upload),
      failed: (upload.failed || 0) > 0,
    }));
    setFinished((prev) => [...items, ...prev.filter((item) => !items.some((i) => i.id === item.id))]);

    if (!awayFromPage()) return;
    setUnseen((count) => count + items.length);
    if (!supported() || Notification.permission !== "granted") return;
    items.forEach((item) => {
      try {
        const notification = new Notification(`${item.name} finished`, {
          body: `${item.text[0].toUpperCase()}${item.text.slice(1)}. Click to open it.`,
          tag: `test-set-${item.id}`,
        });
        notification.onclick = () => {
          window.focus();
          onOpenRef.current(item);
          notification.close();
        };
      } catch {
        return;
      }
    });
  }, [uploads, isActive, nameOf]);

  useEffect(() => {
    const base = baseTitle.current;
    document.title = unseen ? `(${unseen}) Finished · ${base}` : base;
  }, [unseen]);

  useEffect(() => {
    const base = baseTitle.current;
    const seen = () => {
      if (!awayFromPage()) setUnseen(0);
    };
    window.addEventListener("focus", seen);
    document.addEventListener("visibilitychange", seen);
    return () => {
      window.removeEventListener("focus", seen);
      document.removeEventListener("visibilitychange", seen);
      document.title = base;
    };
  }, []);

  const dismiss = (id) => setFinished((prev) => prev.filter((item) => item.id !== id));

  return { finished, dismiss };
}
